<template>
	<v-card>
		<v-card-title class="d-flex align-center justify-space-between">
			{{ $t("UPLOAD") }}
			<v-btn icon variant="flat" @click="close(false)">
				<v-icon> mdi-close </v-icon>
			</v-btn>
		</v-card-title>
		<v-container style="width: 95%" class="ma-4">
			<v-row class="form-row align-center" no-gutters>
				<v-col cols="12" sm="3" class="field-label">{{ $t("FILE_NAME") }}</v-col>
				<v-col cols="12" sm="9" class="field-content">
					<v-file-input v-if="!resolvedType && !default_file_name" :disabled="disableFileInput" variant="outlined"
						density="compact" class="fileInp1 compact-input" v-model="fileArray" :accept="allowedFileTypes" hide-details
						@change="fileInp1Changed()" @click="fileArray = []">
						<template #selection>
							{{ file ? file.name : '' }}
						</template>
					</v-file-input>
					<div v-else class="readonly-value">{{ fileNameForDisplay }}</div>
				</v-col>
			</v-row>

			<v-row class="form-row align-center" no-gutters>
				<v-col cols="12" sm="3" class="field-label">{{ $t("UPLOAD_DATA_TYPE_LABEL") }}</v-col>
				<v-col cols="12" sm="9" class="field-content">
					<v-btn-toggle v-if="isDxfTypeSelectable" v-model="selectedType" divided rounded variant="outlined"
						density="compact" class="type-toggle">
						<v-btn value="design">{{ $t("DESIGN") }}</v-btn>
						<v-btn value="lineworks">{{ $t("LINEWORKS") }}</v-btn>
					</v-btn-toggle>
					<div v-else class="readonly-value">{{ $t(displayTypeKey) }}</div>
				</v-col>
			</v-row>

			<v-row class="form-row align-center" no-gutters>
				<v-col cols="12" sm="3" class="field-label">{{ $t("ASSET_NAME") }}</v-col>
				<v-col cols="12" sm="9" class="field-content">
					<v-text-field variant="outlined" density="compact" class="compact-input" :rules="nameRules" v-model="name"
						required hide-details />
				</v-col>
			</v-row>

			<v-row class="form-row align-center" no-gutters :class="{ 'capture-date-disabled': isLineworksType }">
				<v-col cols="12" sm="3" class="field-label">{{ $t("ASSET_CAPTURE_DATE") }}</v-col>
				<v-col cols="12" sm="9" class="field-content">
					<v-row v-if="isLineworksType" no-gutters>
						<v-col cols="6" class="pr-2">
							<v-text-field variant="outlined" density="compact" class="compact-input" type="text" model-value=""
								disabled readonly hide-details />
						</v-col>
						<v-col cols="6" class="pl-2">
							<v-text-field variant="outlined" density="compact" class="text-center compact-input" type="text"
								model-value="" disabled readonly hide-details />
						</v-col>
					</v-row>
					<v-row v-else no-gutters>
						<v-col cols="6" class="pr-2">
							<v-text-field variant="outlined" density="compact" class="compact-input" type="date" v-model="date"
								required hide-details />
						</v-col>
						<v-col cols="6" class="pl-2">
							<v-text-field variant="outlined" density="compact" type="time" time-format="HH:mm"
								class="text-center compact-input" v-model="time" required hide-details />
						</v-col>
					</v-row>
				</v-col>
			</v-row>

			<distance-unit-field v-if="axis_show" v-model="distanceUnitSelection" />

			<v-row v-if="axis_show" class="form-row align-center" no-gutters>
				<v-col cols="12" sm="3" class="field-label">{{ $t("axis") }}</v-col>
				<v-col cols="12" sm="9" class="field-content">
					<v-select variant="outlined" density="compact" class="compact-input" :items="axis_list" v-model="axis"
						:disabled="disableAxisSelection" required hide-details @update:model-value="updateAxisVal()"></v-select>
				</v-col>
			</v-row>

			<v-row v-if="is_drawing_2d_image" class="form-row align-center" no-gutters>
				<v-col cols="12" sm="3" class="field-label">{{ $t("PROJECTED_GROUND") }}</v-col>
				<v-col cols="12" sm="9" class="field-content">
					<v-switch v-model="showIsDraped" inset hide-details color="success"></v-switch>
				</v-col>
			</v-row>
			<v-row>
				<v-col class="d-flex justify-end align-center actionButtons">
					<v-btn class="previousBtn" rounded @click="goPrevious()">
						{{ $t("PREVIOUS") }}
					</v-btn>
					<v-btn color="primary" class="uploadBtn" id="upload_btn" :disabled="isbtnDisabled" rounded
						@click="uploadData()">
						{{ $t("UPLOAD") }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>
<script>
import DistanceUnitField from "@/components/AddData/DistanceUnitField.vue";
import { isLasPointCloudFile } from "./uploadTypeResolver";
const DISPLAY_TYPE_KEY_MAP = {
	pointcloud: "POINT_CLOUD",
	design: "DESIGN",
	drawing2dImage: "DRAWING_2D_IMAGE",
	lineworks: "LINEWORKS",
	linearfile: "LINEWORKS",
};

export default {
	name: "addTerrainData",
	components: {
		DistanceUnitField,
	},
	props: {
		allowedFileTypes: String,
		axis_show: Boolean,
		is_show: Boolean,
		is_drawing_2d_image: Boolean,
		default_file_name: String,
		selected_file: {
			type: Object,
			default: null,
		},
		resolvedType: String,
	},
	mounted() {
		this.setDateTime();
		this.syncSelectedType();
		if (!this.selected_file && this.default_file_name) {
			this.fileArray[0] = { name: this.default_file_name };
		}
		this.fileInp1Changed();
	},
	data: () => ({
		fileArray: [],
		name: "",
		nameRules: [
			(v) => v.length <= 80 || "Name must be less than 80 characters",
		],
		date: undefined,
		time: undefined,
		axis_list: [
			"ENZ",
			"NEZ",
			"ENZRGB",
			"NEZRGB",
			"ENZiRGB",
			"NEZiRGB",
			"ENZRGBi",
			"NEZRGBi",
		],
		axis: "",
		distanceUnit: "meters",
		showIsDraped: false,
		selectedType: "",
	}),
	computed: {
		/**
		 * 単位選択欄（DistanceUnitField）との橋渡し。
		 * 欄は store と同じ大文字の DistanceUnit を扱い、アップロード body に載る
		 * distanceUnit は従来どおり小文字の wire 形式のまま保つ（未選択は undefined のまま）。
		 *
		 * 現行 3 値（meters / feet / us_survey_feet）は wire 形式が内部定数をそのまま
		 * 小文字化した形なので toUpperCase / toLowerCase で足りる。この対応表は
		 * normalizeDistanceUnit の配列にも同じ 3 値が現れるため、値を増減するときは両方直す。
		 * 小文字化では表せない wire 形式（Landlog API の ftUS 系。siteUnit.types.ts の
		 * DistanceUnitsMap が例）を扱うときは、暗黙の大小変換をやめて明示的な対応表 1 つを
		 * getter / setter / normalizeDistanceUnit で共有する形に変えること
		 */
		distanceUnitSelection: {
			get() {
				return typeof this.distanceUnit === "string" &&
					this.distanceUnit.length > 0
					? this.distanceUnit.toUpperCase()
					: undefined;
			},
			set(value) {
				this.distanceUnit =
					typeof value === "string" ? value.toLowerCase() : value;
			},
		},
		file() {
			if (this.selected_file) {
				return this.selected_file;
			}
			return Array.isArray(this.fileArray) ? this.fileArray[0] : this.fileArray;
		},
		fileNameForDisplay() {
			return this.file?.name || this.default_file_name || "";
		},
		fileExtension() {
			return this.file?.name?.split(".").pop()?.toLowerCase() ?? "";
		},
		displayTypeKey() {
			const type = this.selectedType || this.resolvedType;
			return DISPLAY_TYPE_KEY_MAP[type] || "SELECT_DATA_TYPE";
		},
		isDxfTypeSelectable() {
			return this.resolvedType === "design" && this.fileExtension === "dxf";
		},
		disableAxisSelection() {
			return isLasPointCloudFile(this.file?.name);
		},
		disableFileInput() {
			return !!this.default_file_name || !!this.resolvedType;
		},
		isbtnDisabled() {
			if (
				!this.file ||
				!this.name ||
				(!this.isLineworksType && (!this.date || !this.time)) ||
				!this.selectedType ||
				this.name.length > 80 ||
				/^\s*$/.test(this.name)
			) {
				return true;
			}
			return false;
		},
		isLineworksType() {
			return (
				this.selectedType === "lineworks" || this.selectedType === "linearfile"
			);
		},
	},
	watch: {
		is_show(value) {
			if (value) this.setDateTime();
		},
		default_file_name(value) {
			if (!this.selected_file && value) {
				this.fileArray[0] = { name: value };
				this.fileInp1Changed();
			}
		},
		resolvedType() {
			this.syncSelectedType();
		},
	},
	methods: {
		normalizeDistanceUnit(value) {
			if (typeof value !== "string") {
				return "meters";
			}
			const normalized = value.trim().toLowerCase();
			return ["meters", "feet", "us_survey_feet"].includes(normalized)
				? normalized
				: "meters";
		},
		goPrevious() {
			this.$emit("previous");
		},
		syncSelectedType() {
			if (this.isDxfTypeSelectable) {
				this.selectedType = "design";
				return;
			}
			this.selectedType = this.resolvedType || "";
		},
		open() {
			this.axis = this.axis_list[0];
			this.distanceUnit = this.normalizeDistanceUnit(
				this.$store.state.site?.units?.distance,
			);
			this.$store.state.axis = this.axis_list[0];
		},
		updateAxisVal() {
			this.$store.state.axis = this.axis;
		},
		fileInp1Changed() {
			if (isLasPointCloudFile(this.file?.name)) {
				this.axis = this.axis_list[0];
				this.updateAxisVal();
			}
		},
		close(openLFDialog) {
			if (openLFDialog) {
				const data = [this.file, this.name, this.date, this.time];
				if (this.axis_show) {
					data.push(this.axis);
					data.push(this.distanceUnit);
				}
				if (this.is_drawing_2d_image) {
					data.push(this.showIsDraped);
				}
				data.push(this.selectedType || this.resolvedType || "");
				this.fileArray = [];
				this.name = "";
				this.date = undefined;
				this.time = undefined;
				this.selectedType = "";
				this.$emit("upload", data);
			} else {
				this.fileArray = [];
				this.name = "";
				this.date = undefined;
				this.time = undefined;
				this.distanceUnit = this.normalizeDistanceUnit(
					this.$store.state.site?.units?.distance,
				);
				this.selectedType = "";
				this.$emit("close");
			}
		},
		uploadData() {
			console.log("--START UPLOAD--");
			if (
				!this.name ||
				!this.file ||
				(!this.isLineworksType && (!this.date || !this.time))
			) {
				alert("Please fill all the fields !!!");
			} else {
				this.close(true);
			}
		},
		setDateTime() {
			this.distanceUnit = this.normalizeDistanceUnit(
				this.$store.state.site?.units?.distance,
			);
			const today = new Date();
			// 日付を設定
			this.date =
				today.getFullYear() +
				"-" +
				(today.getMonth() + 1).toString().padStart(2, "0") +
				"-" +
				today.getDate().toString().padStart(2, "0");

			// 時間を設定
			this.time =
				today.getHours().toString().padStart(2, "0") +
				":" +
				today.getMinutes().toString().padStart(2, "0");
		},
	},
};
</script>
<style scoped>
.fileInp1 :deep(.v-text-field__slot) {
	max-width: 100%;
	overflow: hidden;
}

.fileInp1 :deep(.v-file-input__text) {
	width: 100%;
	display: block;
	white-space: nowrap;
	height: 50px;
	line-height: 50px;
}

.uploadBtn {
	margin-bottom: 3%;
}

.previousBtn {
	margin-bottom: 3%;
	margin-right: 12px;
	background-color: #565664;
	color: #ffffff;
}

.actionButtons {
	gap: 0;
}

.form-row {
	margin-bottom: 10px;
}

.field-label {
	font-size: 14px;
	font-weight: 500;
	padding-right: 6px;
	min-height: 32px;
	display: flex;
	align-items: center;
}

.field-content {
	min-height: 32px;
	display: flex;
	align-items: center;
}

.compact-input {
	width: 100%;
}

.readonly-value {
	font-size: 14px;
	line-height: 1.4;
	word-break: break-all;
	padding: 4px 0;
}

.capture-date-disabled {
	opacity: 0.55;
}

.type-toggle {
	border-radius: 24px !important;
}

:deep(.type-toggle .v-btn.v-btn--active) {
	background-color: #565664 !important;
}
</style>